/**
 * Copia texto para a área de transferência, robusto no embed. O web-chat roda
 * como iframe cross-origin e o `<iframe>` do host NÃO pede `allow="clipboard-write"`
 * de propósito (contrato em `apps/clients/web-chat/AGENTS.md`): nesse contexto a
 * Clipboard API (`navigator.clipboard.writeText`) REJEITA com `NotAllowedError`,
 * e em origem insegura ela nem existe. Então: tenta a Clipboard API e, se ela
 * faltar ou for bloqueada, cai no `execCommand('copy')` legado, que NÃO é regido
 * pela Permissions Policy e funciona dentro do gesto do usuário (a ativação
 * transitória sobrevive ao await curto da rejeição). Sempre best-effort: nunca
 * lança; devolve `true` se algum caminho copiou.
 *
 * Mora no kit (e não no host) porque o voucher e os cards copiam daqui de dentro
 * e o kit não pode importar do app; o trip-panel e o web-chat re-exportam ESTE
 * módulo — um só caminho de cópia no produto (#2976).
 *
 * Deve ser chamado a partir de um handler de gesto do usuário (click/tap).
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    // `lib.dom` afirma que `navigator.clipboard` existe; em origem insegura ele
    // não existe e ler `.writeText` dele lança um `TypeError` SÍNCRONO.
    // `Reflect.get` tira o tipo do caminho e devolve o `undefined` do browser.
    const clipboard = Reflect.get(navigator, 'clipboard') as Clipboard | undefined;
    if (clipboard) {
      await clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API bloqueada no iframe → fallback legado abaixo.
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    try {
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      // execCommand é deprecado, mas é o único caminho de cópia que ainda funciona
      // no iframe cross-origin sem `allow="clipboard-write"` — é o fallback de fato.
      const execCommand = Reflect.get(document, 'execCommand') as
        | ((commandId: string) => boolean)
        | undefined;
      return execCommand?.call(document, 'copy') ?? false;
    } finally {
      // Sai SEMPRE, lance quem lançar: um textarea fixo e invisível esquecido no
      // body seguraria o foco da página depois de uma cópia que falhou.
      ta.remove();
    }
  } catch {
    return false;
  }
}

/**
 * Quais caminhos de cópia EXISTEM neste aparelho — o diagnóstico que acompanha
 * a telemetria de `copy_failed` (#2976, item 5). Sonda com `Reflect.get` pelo
 * mesmo motivo de `copyText`: `lib.dom` afirma que ambos existem, e o browser
 * discorda (origem insegura sem Clipboard API; navegador que já removeu o
 * `execCommand`). Só booleanos: nada do texto copiado sai daqui.
 */
export function copyPaths(): { clipboardApi: boolean; execCommand: boolean } {
  // Mesmo cast de `copyText`: sem ele o tipo diz `Clipboard` e a comparação com
  // `undefined` vira condição "impossível" para o lint — mas é a que importa.
  const clipboard = Reflect.get(navigator, 'clipboard') as Clipboard | undefined;
  const execCommand: unknown = Reflect.get(document, 'execCommand');
  return { clipboardApi: clipboard !== undefined, execCommand: typeof execCommand === 'function' };
}
